"use client"
import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import BulletList from "@/app/(site)/_components/Molecules/BulletList"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { debounce } from "@/app/(site)/_utils/debounce"
import { getImageSrc } from "@/app/(site)/_utils/getImageSrc"
import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"
import React, { useCallback, useEffect, useState } from "react"
import { useProductDisplayInfo } from "../helper"

type SubBigProductCardProps = {
    productVariants: MinimizedCentraProduct[]
    override?: { image?: { url: string; alt: string } }
    lazyLoad?: boolean
}

const SubBigProductCard: React.FC<SubBigProductCardProps> = ({
    productVariants,
    override,
    lazyLoad = true
}) => {
    const {
        uri,
        badges,
        colorVariantsAmount,
        benefits,
        name,
        anyVariantisAvailable,
        price,
        priceBeforeDiscount,
        showAsOnSale,
        productImageAlt,
        imageSources
    } = useProductDisplayInfo(productVariants, override)
    const customImageSize =
        typeof window !== "undefined" && window.innerWidth <= 500
            ? window.innerWidth
            : 1441

    // Use default image based on server-side width estimation
    // Initialize image sources (ensure imageSources exists)
    const initialImageSrc =
        typeof window !== "undefined"
            ? getImageSrc(window.innerWidth, imageSources)
            : getImageSrc(1440, imageSources) // Fallback for SSR

    const initialHoverImageSrc =
        typeof window !== "undefined" && window.innerWidth <= 1440
            ? imageSources?.small?.[1]
            : imageSources?.standard?.[1]

    const [imageSrc, setImageSrc] = useState<string>(initialImageSrc)
    const [hoverImageSrc, setHoverImageSrc] = useState<string>(initialHoverImageSrc)
    const [isHydrated, setIsHydrated] = useState(false)

    useEffect(() => {
        setIsHydrated(true)
    }, [])

    const handleResize = useCallback(() => {
        if (imageSources) {
            setImageSrc(getImageSrc(window.innerWidth, imageSources))
            setHoverImageSrc(
                window.innerWidth <= 1440
                    ? imageSources?.small?.[1]
                    : imageSources?.standard?.[1]
            )
        }
    }, [imageSources])

    // Update image based on window size, but only after hydration
    useEffect(() => {
        if (isHydrated && typeof window !== "undefined") {
            handleResize()
            const debouncedHandleResize = debounce(handleResize, 250)
            window.addEventListener("resize", debouncedHandleResize)

            return () => {
                window.removeEventListener("resize", debouncedHandleResize)
            }
        }
    }, [isHydrated, handleResize, imageSources])

    const handleMouseEnter = () => {
        setImageSrc(hoverImageSrc || initialImageSrc)
    }

    const handleMouseLeave = () => {
        setImageSrc(getImageSrc(customImageSize, imageSources))
    }

    return (
        <div
            className="group relative"
            data-search-result-id={productVariants?.[0]?.search_result_id}
            data-product-listing-result-id={
                productVariants?.[0]?.product_listing_result_id
            }
            data-recommendation-id={productVariants?.[0]?.recommendation_id}
        >
            <div className="bg-[#f4f5f5]">
                {Array.isArray(badges) && badges.length > 0 && (
                    <div className="absolute left-0 right-0 top-0 z-[5] flex flex-wrap gap-2">
                        {badges.map((badge, index) => (
                            <Badge key={index} badge={badge} />
                        ))}
                    </div>
                )}
                <DynamicLink href={uri} prefetch={false}>
                    <div className="relative mb-16 aspect-square overflow-hidden">
                        <BaseImage
                            key={isHydrated ? "client" : "server"}
                            fill
                            src={imageSrc}
                            loading={lazyLoad ? "eager" : "lazy"}
                            priority
                            alt={productImageAlt}
                            className="object-contain"
                            onMouseEnter={() => hoverImageSrc && handleMouseEnter()}
                            onMouseLeave={handleMouseLeave}
                        />
                    </div>
                </DynamicLink>
            </div>
            <div className="flex flex-row">
                <div className="w-[100%] sm:w-[50%]">
                    <DynamicLink href={uri} prefetch={false}>
                        <Text
                            size="small"
                            weight="regular"
                            className={`md:text-16 ${!anyVariantisAvailable && ""}`}
                        >
                            {name}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="mb-8 flex gap-8 text-main-dark-grey md:text-16"
                        >
                            {price}
                            {showAsOnSale && (
                                <span className="line-through">
                                    {priceBeforeDiscount}
                                </span>
                            )}
                        </Text>
                    </DynamicLink>
                    <div className="h-38 w-full">
                        {colorVariantsAmount > 0 && (
                            <Text
                                size="small"
                                weight="regular"
                                className={`text-main-dark-grey md:text-16`}
                            >
                                {colorVariantsAmount} Color
                                {colorVariantsAmount > 1 ? "s" : ""}
                            </Text>
                        )}
                    </div>
                </div>
                <div className="w-[100%] sm:w-[50%]">
                    {benefits.length > 0 && <BulletList title={benefits} />}
                </div>
            </div>
        </div>
    )
}

export default SubBigProductCard
